// import { Container, EstimatedCbamLiabilityWidget, LoadingScreen } from '@/shared/components'
import { Container, LoadingScreen } from '@/shared/components'
import { NetMassDisplayUnitToggle } from '@/shared/components/NetMassDisplayUnitToggle'
import {
  NetMassDisplayUnitProvider,
  useNetMassDisplay
} from '@/shared/netMassDisplay/NetMassDisplayUnitContext'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import {
  BreadcrumbsBar,
  Button,
  DataBlock,
  H2,
  MissingResource,
  PageSection,
  PromoBanner
} from '@/common'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import CbamSimulationGoodsTable from './components/CbamSimulationGoodsTable'
import {
  CalculatorIcon,
  ClockArrowIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon
} from '@/common/components/icons'
import { CbamSimulationGood } from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import { QUARTERS, type Quarter } from '@/config/cbamReportingPeriods'
import useDialog from '@/common/hooks/useDialog'
import SimulationGoodCreateDialog from './dialogs/CbamSimulationGoodCreateDialog'
import SimulationGoodEditDialog from './dialogs/CbamSimulationGoodEditDialog'
import CbamSimulationGoodDeleteDialog from './dialogs/CbamSimulationGoodDeleteDialog'
import DeleteCbamSimulationDialog from '../../dialogs/DeleteCbamSimulationDialog'
import EditCbamSimulationDialog from '../../dialogs/EditCbamSimulationDialog'
import CbamSimulationDisclaimerDialog from './dialogs/CbamSimulationDisclaimerDialog'
import toast from 'react-hot-toast'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'
import { useTrackCbamSimulationDetailsViewedWhenReady } from '@/features/cbam-simulations-v2/hooks/useCbamSimulationClarityTracking'
import { useSimulationWithLiability } from '@/features/cbam-simulations-v2/hooks/useSimulationWithLiabilty'
import { useUpdateCbamSimulation } from '@/features/cbam-simulations-v2/hooks/cbamSimulations'
import EditCbamModifiersDrawer from '@/shared/components/EditCbamModifiersDrawer'
import CertificatesAndLiabilityPerQuarter from './components/CertificatesAndLiabilityPerQuarter'
import ScenariosTotalsRow from './components/ScenariosTotalsRow'
import { getSimulationYearTotals } from './utils/simulationYearTotals'
import EuaEmissionPriceOverviewWidget from '@/features/cbam-reporting/components/EuaEmissionPriceOverviewWidget'
import SimulationGoodDetailsDrawer from './components/SimulationGoodDetailsDrawer'
import type { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { useGetAllSupplierEmissionsReportsForOrganization } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
const DISCLAIMER_SHOWN_KEY = 'cbam-simulation-disclaimer-shown'

function SimulationTotalVolumeDataBlock({
  label,
  totalVolumeTonnes
}: {
  label: string
  totalVolumeTonnes: number
}) {
  const { formatNetMass } = useNetMassDisplay()
  return <DataBlock label={label} data={formatNetMass(totalVolumeTonnes)} />
}

function hasDisclaimerBeenShown(simulationId: string): boolean {
  const shownSimulations = localStorage.getItem(DISCLAIMER_SHOWN_KEY)
  if (!shownSimulations) {
    return false
  }
  const parsed = JSON.parse(shownSimulations) as string[]
  return parsed.includes(simulationId)
}

function markDisclaimerAsShown(simulationId: string): void {
  const shownSimulations = localStorage.getItem(DISCLAIMER_SHOWN_KEY)
  const parsed = shownSimulations ? (JSON.parse(shownSimulations) as string[]) : []
  if (!parsed.includes(simulationId)) {
    parsed.push(simulationId)
    localStorage.setItem(DISCLAIMER_SHOWN_KEY, JSON.stringify(parsed))
  }
}

export default function CbamSimulationDetailsPage({ simulationYear }: { simulationYear: number }) {
  const { t, shortDateFormatter, numberFormatter3Decimals } = useI18n()

  const formatDirectEmissions = (value: number) =>
    value > 0 ? `${numberFormatter3Decimals(value)} tCO₂e` : '–'
  const navigate = useNavigate()
  const { id: organizationId } = useSession().session.organization
  const cbamSimulationId = useParams().cbamSimulationId!

  const {
    simulation: simulationFromList,
    liabilityData,
    liabilityDataBaseLow,
    liabilityDataHighForScenario,
    isBatchLiabilityLoading,
    importedGoodsExtended,
    isCbamSimulationGoodsLoading
  } = useSimulationWithLiability(organizationId, cbamSimulationId, {
    productionMethodCode: ''
  })
  const simulation = simulationFromList

  const { mutateAsync: updateSimulation, isPending: isUpdatingModifiers } = useUpdateCbamSimulation(
    organizationId,
    simulation?.id ?? ''
  )

  const handleSaveSimulationModifiers = useCallback(
    async (modifiers: CbamModifiers) => {
      if (!simulation) {
        return
      }
      trackCbamSimulationSignal('simulations:modifiers:submitted')
      try {
        await updateSimulation({ name: simulation.name, modifiers })
        trackCbamSimulationSignal('simulations:modifiers:succeeded')
        toast.success(t('cbamModifiers:editVariables.notifications.success'))
      } catch (error) {
        console.error(error)
        trackCbamSimulationSignal('simulations:modifiers:failed')
        toast.error(t('cbamModifiers:editVariables.notifications.error'))
        throw error
      }
    },
    [simulation, t, updateSimulation]
  )

  const {
    open: openCreateSimulationGood,
    openDialog: openCreateSimulationGoodDialog,
    closeDialog: closeCreateSimulationGoodDialog
  } = useDialog()

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [goodToEdit, setGoodToEdit] = useState<CbamSimulationGood | null>(null)

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [goodToDelete, setGoodToDelete] = useState<CbamSimulationGood | null>(null)
  const [goodToInspect, setGoodToInspect] = useState<CbamSimulationGood | null>(null)

  const [isDeleteSimulationDialogOpen, setIsDeleteSimulationDialogOpen] = useState(false)
  const [isEditSimulationDialogOpen, setIsEditSimulationDialogOpen] = useState(false)
  const [isEditVariablesDrawerOpen, setIsEditVariablesDrawerOpen] = useState(false)
  const [isDisclaimerDialogOpen, setIsDisclaimerDialogOpen] = useState(false)

  const supplierEmissionsReportsQuery =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)

  const cbamSimulationGoods = useMemo(
    () =>
      importedGoodsExtended.map(simulationGoodRow => {
        const { emissionsReport: unusedEmissionsReport, ...good } = simulationGoodRow
        void unusedEmissionsReport
        return good
      }),
    [importedGoodsExtended]
  )
  const supplierEmissionsReportsData = supplierEmissionsReportsQuery.data

  const supplierEmissionsReportForInspect = useMemo(() => {
    if (!goodToInspect?.supplierEmissionsReportId || !supplierEmissionsReportsData) {
      return null
    }
    return (
      supplierEmissionsReportsData.find(
        report => report.id === goodToInspect.supplierEmissionsReportId
      ) ?? null
    )
  }, [goodToInspect, supplierEmissionsReportsData])

  const goodInspectLiabilityData = useMemo(() => {
    if (!goodToInspect) {
      return null
    }
    const results = liabilityDataBaseLow?.results ?? []
    return results.find(result => result.id === goodToInspect.id) ?? null
  }, [goodToInspect, liabilityDataBaseLow])

  const { data: goodsCodes } = useGoodsCodes({ aggregatedGoodsCategories: undefined })
  const inspectAggregatedGoodsCategory = useMemo(() => {
    if (!goodToInspect) {
      return undefined
    }
    return goodsCodes?.find(goodsCode => goodsCode.goodsCode === goodToInspect.cnCode)?.aggregatedGoodsCategory
  }, [goodToInspect, goodsCodes])

  const quartersWithGoods = useMemo(() => {
    const quarters = new Set<Quarter>()
    for (const good of cbamSimulationGoods) {
      if (good.quarter != null && QUARTERS.includes(good.quarter)) {
        quarters.add(good.quarter)
      }
    }
    return Array.from(quarters)
  }, [cbamSimulationGoods])

  const yearTotals = useMemo(
    () =>
      getSimulationYearTotals(
        liabilityDataBaseLow,
        simulation?.modifiers,
        simulationYear,
        quartersWithGoods,
        liabilityDataHighForScenario
      ),
    [
      liabilityDataBaseLow,
      liabilityDataHighForScenario,
      simulation?.modifiers,
      simulationYear,
      quartersWithGoods
    ]
  )

  const liabilityByGoodId = useMemo(() => {
    const map = new Map<string, BatchLiabilityResultItem>()
    for (const item of liabilityData?.results ?? []) {
      if (item.id != null) {
        map.set(item.id, item)
      }
    }
    return map
  }, [liabilityData?.results])

  useEffect(() => {
    if (!hasDisclaimerBeenShown(cbamSimulationId)) {
      setIsDisclaimerDialogOpen(true)
    }
  }, [cbamSimulationId])

  const handleCloseDisclaimerDialog = () => {
    markDisclaimerAsShown(cbamSimulationId)
    setIsDisclaimerDialogOpen(false)
  }

  const handleEditGood = (good: CbamSimulationGood) => {
    setGoodToEdit(good)
    setIsEditDialogOpen(true)
  }

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false)
    setGoodToEdit(null)
  }

  const handleDeleteGood = (good: CbamSimulationGood) => {
    setGoodToDelete(good)
    setIsDeleteDialogOpen(true)
  }

  const handleInspectGood = (good: CbamSimulationGood) => {
    setGoodToInspect(good)
  }

  const handleCloseInspectDrawer = () => {
    setGoodToInspect(null)
  }

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false)
    setGoodToDelete(null)
  }

  const handleCloseDeleteSimulationDialog = () => {
    setIsDeleteSimulationDialogOpen(false)
  }

  const handleDeleteSimulationSuccess = () => {
    navigate('/cbam-simulations')
  }

  const areResourcesLoading =
    isCbamSimulationGoodsLoading || supplierEmissionsReportsQuery.isLoading

  useTrackCbamSimulationDetailsViewedWhenReady({
    cbamSimulationId,
    areResourcesLoading,
    hasSimulationData: Boolean(simulation)
  })

  if (areResourcesLoading) {
    return <LoadingScreen />
  }

  if (!simulation) {
    return <Navigate to="/cbam-simulations" />
  }

  const simulationName = simulation?.name ?? ''

  return (
    <NetMassDisplayUnitProvider>
      <BreadcrumbsBar
        links={[
          { label: t('common:breadcrumbs.cbamSimulations'), to: '/cbam-simulations' },
          { label: simulationName, to: '' }
        ]}
      />
      <Container>
        <header className="flex justify-between items-center">
          <H2>{simulationName}</H2>
          <div className="flex items-center gap-3">
            <NetMassDisplayUnitToggle />
            <span className="flex items-center gap-1.5 text-text-secondary">
              <ClockArrowIcon className="size-6 shrink-0" />
              {t('cbamSimulations:cbamSimulationDetails.lastEditedOn', {
                date: shortDateFormatter(new Date(simulation.updatedAt ?? simulation.createdAt))
              })}
            </span>
            <div className="flex gap-2">
              <Button
                variant="subtle"
                icon={<PencilIcon />}
                onClick={() => setIsEditSimulationDialogOpen(true)}
              >
                {t('cbamSimulations:cbamSimulationDetails.editNameButton')}
              </Button>
              <Button
                variant="danger"
                icon={<TrashIcon />}
                onClick={() => setIsDeleteSimulationDialogOpen(true)}
              >
                {t('common:actions.delete')}
              </Button>
            </div>
          </div>
        </header>
        {cbamSimulationGoods.length === 0 && (
          <PromoBanner
            title={t('cbamSimulations:cbamSimulationDetails.startSimulationBanner.title')}
            description={t(
              'cbamSimulations:cbamSimulationDetails.startSimulationBanner.description'
            )}
            action={
              <Button
                icon={<PlusIcon className="size-6" />}
                onClick={openCreateSimulationGoodDialog}
              >
                {t('cbamSimulations:cbamSimulationDetails.startSimulationBanner.action')}
              </Button>
            }
          />
        )}
        {cbamSimulationGoods.length > 0 && (
          <PromoBanner
            title={t('cbamSimulations:cbamSimulationDetails.editVariablesBanner.title')}
            description={t('cbamSimulations:cbamSimulationDetails.editVariablesBanner.description')}
            action={
              <Button
                icon={<CalculatorIcon className="size-6" />}
                onClick={() => setIsEditVariablesDrawerOpen(true)}
              >
                {t('cbamSimulations:cbamSimulationDetails.editVariablesBanner.action')}
              </Button>
            }
          />
        )}
        <ScenariosTotalsRow
          totalVolume={yearTotals.totalVolume}
          totalGoods={cbamSimulationGoods.length}
          totalCertificates={yearTotals.totalCertificates}
          totalLiability={yearTotals.totalLiabilityBase}
          totalCertificatesHigh={yearTotals.totalCertificatesHigh}
          cscfPercentageFromModifiers={
            simulation?.modifiers?.cscfPercentage != null &&
            simulation.modifiers.cscfPercentage !== ''
              ? parseFloat(simulation.modifiers.cscfPercentage)
              : undefined
          }
          totalLiabilityLow={yearTotals.totalLiabilityLow}
          totalLiabilityHigh={yearTotals.totalLiabilityHigh}
        />
        <div className="grid grid-cols-4 gap-6">
          <div className="flex flex-col gap-2">
            <DataBlock
              label={t('cbamSimulations:cbamSimulationDetails.summaryTotals.totalEmissions')}
              data={formatDirectEmissions(yearTotals.totalDirectEmissions)}
            />
            <SimulationTotalVolumeDataBlock
              label={t('cbamSimulations:cbamSimulationDetails.summaryTotals.totalVolume')}
              totalVolumeTonnes={yearTotals.totalVolume}
            />
            <DataBlock
              label={t('cbamSimulations:cbamSimulationDetails.summaryTotals.totalGoods')}
              data={cbamSimulationGoods.length > 0 ? String(cbamSimulationGoods.length) : '–'}
            />
          </div>
          <div className="col-span-3">
            <EuaEmissionPriceOverviewWidget toggleButtonSize="xs" />
          </div>
        </div>
        <PageSection
          title={t('cbamSimulations:cbamSimulationDetails.simulationGoodsListTitle')}
          actions={
            <Button icon={<PlusIcon className="size-6" />} onClick={openCreateSimulationGoodDialog}>
              {t('cbamSimulations:cbamSimulationDetails.startSimulationBanner.action')}
            </Button>
          }
        >
          {cbamSimulationGoods.length > 0 ? (
            <CbamSimulationGoodsTable
              cbamSimulationGoods={cbamSimulationGoods}
              onInspectGood={handleInspectGood}
              liabilityByGoodId={liabilityByGoodId}
              projectionYear={String(simulationYear)}
              onEditGood={handleEditGood}
              onDeleteGood={handleDeleteGood}
            />
          ) : (
            <MissingResource
              title={t('cbamSimulations:cbamSimulationDetails.noGoodsFound.title')}
              description={t('cbamSimulations:cbamSimulationDetails.noGoodsFound.description')}
              onClick={openCreateSimulationGoodDialog}
              callToAction={t('cbamSimulations:cbamSimulationDetails.noGoodsFound.callToAction')}
            />
          )}
        </PageSection>
        {cbamSimulationGoods.length > 0 && (
          <PageSection
            title={t('cbamModifiers:certificatesAndLiabilityPerQuarter.sectionTitle')}
            actions={
              <Button onClick={() => setIsEditVariablesDrawerOpen(true)} icon={<CalculatorIcon />}>
                {t('cbamSimulations:cbamSimulationDetails.editVariables.button')}
              </Button>
            }
          >
            <CertificatesAndLiabilityPerQuarter
              modifiers={simulation.modifiers}
              simulationYear={simulationYear}
              quartersWithGoods={quartersWithGoods}
              aggregatedProjectionByQuarter={liabilityData?.aggregatedProjectionByQuarter}
              aggregatedProjectionByQuarterHigh={
                liabilityDataHighForScenario?.aggregatedProjectionByQuarter
              }
              isLoading={isBatchLiabilityLoading}
            />
          </PageSection>
        )}
      </Container>
      <SimulationGoodCreateDialog
        simulationYear={simulationYear}
        isOpen={openCreateSimulationGood}
        close={closeCreateSimulationGoodDialog}
        cbamSimulationId={cbamSimulationId}
      />
      {goodToEdit && (
        <SimulationGoodEditDialog
          simulationYear={simulationYear}
          isOpen={isEditDialogOpen}
          close={handleCloseEditDialog}
          cbamSimulationId={cbamSimulationId}
          simulationGood={goodToEdit}
        />
      )}
      {goodToDelete && (
        <CbamSimulationGoodDeleteDialog
          isOpen={isDeleteDialogOpen}
          close={handleCloseDeleteDialog}
          cbamSimulationId={cbamSimulationId}
          simulationGood={goodToDelete}
        />
      )}
      <DeleteCbamSimulationDialog
        isOpen={isDeleteSimulationDialogOpen}
        close={handleCloseDeleteSimulationDialog}
        simulationName={simulationName}
        simulationId={cbamSimulationId}
        onAfterLeave={handleDeleteSimulationSuccess}
      />
      <EditCbamSimulationDialog
        isOpen={isEditSimulationDialogOpen}
        close={() => setIsEditSimulationDialogOpen(false)}
        simulationName={simulationName}
        simulationId={cbamSimulationId}
      />
      <CbamSimulationDisclaimerDialog
        isOpen={isDisclaimerDialogOpen}
        close={handleCloseDisclaimerDialog}
      />
      {simulation && (
        <EditCbamModifiersDrawer
          year={simulationYear}
          isOpen={isEditVariablesDrawerOpen}
          close={() => setIsEditVariablesDrawerOpen(false)}
          initialModifiers={simulation.modifiers}
          syncSource={simulation}
          isSaving={isUpdatingModifiers}
          onSave={handleSaveSimulationModifiers}
        />
      )}
      {goodToInspect && (
        <SimulationGoodDetailsDrawer
          isOpen={!!goodToInspect}
          close={handleCloseInspectDrawer}
          simulationGood={goodToInspect}
          liabilityData={goodInspectLiabilityData}
          supplierEmissionsReport={supplierEmissionsReportForInspect}
          simulationYear={simulationYear}
          modifiers={simulation?.modifiers ?? null}
          aggregatedGoodsCategory={inspectAggregatedGoodsCategory}
        />
      )}
    </NetMassDisplayUnitProvider>
  )
}
